import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Crown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/gado";
import { useAuth } from "@/context/AuthContext";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | error | timeout
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch {
        /* keep trying */
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
  }, [refresh]);

  return (
    <div className="min-h-screen grid place-items-center bg-gado-cream p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full rounded-2xl border border-gado-line bg-white p-10 text-center"
        data-testid="payment-success-page"
      >
        {state === "checking" && (
          <>
            <div className="h-12 w-12 mx-auto rounded-full border-2 border-gado-green border-t-transparent animate-spin mb-6" />
            <h1 className="font-cabinet text-2xl font-bold text-gado-ink">Confirmando pagamento…</h1>
            <p className="text-gado-muted mt-2">Aguarde alguns segundos.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle weight="fill" size={64} className="mx-auto text-gado-green mb-5" />
            <h1 className="font-cabinet text-2xl font-bold text-gado-ink">Bem-vindo ao Pro! <Crown weight="fill" className="inline text-gado-gold" /></h1>
            <p className="text-gado-muted mt-2">Sua assinatura está ativa. Aproveite os PDFs ilimitados e relatórios mensais.</p>
            <Button onClick={() => navigate("/app")} data-testid="go-dashboard-button" className="w-full mt-6 bg-gado-green hover:bg-gado-greenHover text-white">
              Ir para o painel
            </Button>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle weight="fill" size={64} className="mx-auto text-destructive mb-5" />
            <h1 className="font-cabinet text-2xl font-bold text-gado-ink">
              {state === "timeout" ? "Ainda processando" : "Algo deu errado"}
            </h1>
            <p className="text-gado-muted mt-2">
              {state === "timeout"
                ? "O pagamento pode levar alguns instantes. Atualize o painel em breve."
                : "Não conseguimos confirmar o pagamento."}
            </p>
            <Button onClick={() => navigate("/app")} className="w-full mt-6 bg-gado-green hover:bg-gado-greenHover text-white">
              Voltar ao painel
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
